import api from "../apiClient";

const RETRYABLE_CODES = new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"]);

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isRetryableError = (error) => {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.code || "").toUpperCase();
  return !status || status >= 500 || RETRYABLE_CODES.has(code);
};

const withRetry = async (task, retries = 0, retryDelayMs = 250) => {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isRetryableError(error)) {
        throw error;
      }
      await sleep(retryDelayMs);
    }
  }

  throw lastError;
};

export const listCrmLeads = (params = {}) =>
  api.get("/leads/crm-leads/", { params });

export const getLeadPipeline = (params = {}) =>
  api.get("/leads/pipeline/", { params });

export const getCrmLead = (id) => api.get(`/leads/crm-leads/${id}/`);

export const getLeadAttribution = (id) => api.get(`/leads/${id}/attribution/`);

// Nearby customers within a few miles of the lead's address — route context.
export const getLeadNearby = (id, miles = 5) =>
  api.get(`/leads/crm-leads/${id}/nearby/`, { params: { miles } });

// Stage-aware context: last message, latest quote, next appointment, vertical.
export const getLeadQueueContext = (id) =>
  api.get(`/leads/crm-leads/${id}/queue-context/`);

export const createCrmLead = (payload) => api.post("/leads/crm-leads/", payload);

export const updateCrmLead = (id, payload) =>
  api.patch(`/leads/crm-leads/${id}/`, payload);

export const archiveCrmLead = (id, payload = {}) =>
  api.patch(`/leads/crm-leads/${id}/`, { ...payload, archived: true });

export const spamCrmLead = (id, attributes = {}) =>
  api.patch(`/leads/crm-leads/${id}/`, {
    archived: true,
    attributes: { ...attributes, spam: true },
  });

export const updateLeadStage = (id, pipelineStage, extraFields = {}) =>
  withRetry(
    () =>
      api.patch(
        `/leads/${id}/stage/`,
        { pipeline_stage: pipelineStage, ...extraFields },
        { timeout: 10000 }
      ),
    2
  );

// OUTCOME / STATUS action — data only, sends nothing. Accepts any of:
// { stage, outcome_note, last_disposition, do_not_contact }. When
// do_not_contact is true the backend writes the suppression ledger and opts
// the lead out (the clean exit) — no message is ever dispatched here.
export const setLeadOutcome = (id, payload = {}) =>
  withRetry(() => api.post(`/leads/${id}/outcome/`, payload, { timeout: 10000 }), 2);

export const listWebLeads = (params = {}) =>
  api.get("/leads/web-leads/", { params });

export const archiveWebLead = (webLeadId) =>
  api.post(`/leads/web/${webLeadId}/archive/`);

export const spamWebLead = (webLeadId) =>
  api.post(`/leads/web/${webLeadId}/spam/`);

export const transferLead = (payload) => {
  if (typeof payload === "number") {
    return api.post("/leads/transfer-lead/", { lead_id: payload });
  }
  return api.post("/leads/transfer-lead/", payload || {});
};
